'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getNotificationList,
  type NotificationListItem,
  type NotificationTab,
  readAllNotifications,
} from '@/lib/api/notification'
import { inboxAnalytics, toInboxTabType } from '@/lib/utils/analytics'
import { useNotificationStore } from '@/store/notificationStore'

const PAGE_SIZE = 20

export function useNotifications(tab: NotificationTab) {
  const [items, setItems] = useState<NotificationListItem[]>([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const requestVersionRef = useRef(0)
  const pollInFlightRef = useRef(false)
  const refreshUnread = useNotificationStore((state) => state.refreshUnread)
  const markTabRead = useNotificationStore((state) => state.markTabRead)

  const readAll = useCallback(async () => {
    const response = await readAllNotifications(tab)
    if (response.code !== 200 || !response.data) throw new Error(response.msg)

    setItems((current) => current.map((item) => (item.read ? item : { ...item, read: true })))
    markTabRead(tab)
    void refreshUnread().catch((error) =>
      console.error('Failed to refresh notification unread state:', error),
    )
    return response.data.updated
  }, [markTabRead, refreshUnread, tab])

  const markItemRead = useCallback(
    (id: string) => {
      setItems((current) => {
        const next = current.map((item) => (item.id === id ? { ...item, read: true } : item))
        if (!next.some((item) => !item.read)) markTabRead(tab)
        return next
      })
      void refreshUnread().catch((error) =>
        console.error('Failed to refresh notification unread state:', error),
      )
    },
    [markTabRead, refreshUnread, tab],
  )

  useEffect(() => {
    requestVersionRef.current += 1
    const requestVersion = requestVersionRef.current
    const controller = new AbortController()

    setItems([])
    setPage(1)
    setHasMore(false)
    setLoading(true)

    void refreshUnread(controller.signal).catch((error) => {
      if (!controller.signal.aborted)
        console.error('Failed to load notification unread state:', error)
    })

    void getNotificationList({ tab, page: 1, size: PAGE_SIZE }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted || requestVersion !== requestVersionRef.current) return
        if (response.code !== 200 || !response.data) return

        setItems(response.data.records)
        setPage(Number(response.data.current) || 1)
        setHasMore(Number(response.data.current) < Number(response.data.pages))

        // Tab 成功展示（首屏列表返回）后上报；含默认 System / 空列表
        inboxAnalytics.tabViewed(toInboxTabType(tab))
      })
      .catch((error) => {
        if (!controller.signal.aborted) console.error('Failed to load notifications:', error)
      })
      .finally(() => {
        if (!controller.signal.aborted && requestVersion === requestVersionRef.current) {
          setLoading(false)
        }
      })

    return () => controller.abort()
  }, [refreshUnread, tab])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    const requestVersion = requestVersionRef.current
    const nextPage = page + 1
    setLoading(true)

    try {
      const response = await getNotificationList({ tab, page: nextPage, size: PAGE_SIZE })
      if (requestVersion !== requestVersionRef.current || response.code !== 200 || !response.data) {
        return
      }

      setItems((current) => [...current, ...response.data.records])
      setPage(Number(response.data.current) || nextPage)
      setHasMore(Number(response.data.current) < Number(response.data.pages))
    } catch (error) {
      console.error('Failed to load more notifications:', error)
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false)
    }
  }, [hasMore, loading, page, tab])

  const refresh = useCallback(async () => {
    const requestVersion = requestVersionRef.current
    void refreshUnread().catch((error) =>
      console.error('Failed to refresh notification unread state:', error),
    )
    const response = await getNotificationList({ tab, page: 1, size: PAGE_SIZE })
    if (requestVersion !== requestVersionRef.current || response.code !== 200 || !response.data) {
      return
    }

    setItems(response.data.records)
    setPage(Number(response.data.current) || 1)
    setHasMore(Number(response.data.current) < Number(response.data.pages))
  }, [refreshUnread, tab])

  const poll = useCallback(async () => {
    if (pollInFlightRef.current) return

    pollInFlightRef.current = true
    const requestVersion = requestVersionRef.current
    try {
      await refreshUnread()
      const response = await getNotificationList({ tab, page: 1, size: PAGE_SIZE })
      if (requestVersion !== requestVersionRef.current || response.code !== 200 || !response.data) {
        return
      }

      setItems(response.data.records)
    } finally {
      pollInFlightRef.current = false
    }
  }, [refreshUnread, tab])

  return { items, loading, hasMore, loadMore, readAll, refresh, poll, markItemRead }
}
